import React, { useState, useEffect, useRef } from 'react';

interface EmailValidatedToCheckExistComponentProps {
    email: string;
    setEmail: (value: string) => void;
    className?: any;
    getEmail: (email: string) => Promise<boolean>;
    touched: boolean;
    setTouched: (touched: boolean) => void;
    setIsEmailExist?: (value: boolean | null) => void;
}

const EmailValidatedToCheckExistComponent = ({
    email,
    setEmail,
    getEmail,
    className = {
        parentDiv: "",
        label: "font-semibold text-sm",
        input: "w-full rounded py-2 px-4 mt-2 border-2 text-sm text-black outline-tertiary"
    },
    touched,
    setTouched,
    setIsEmailExist
}: EmailValidatedToCheckExistComponentProps) => {
    const [isEmailAvailable, setIsEmailAvailable] = useState<boolean | null>(null);
    const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
    const [initialEmail, setInitialEmail] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setInitialEmail(email);
    }, [email]);

    useEffect(() => {
        let debounceTimer: NodeJS.Timeout;

        if (touched && email?.trim() !== '' && email?.trim() !== initialEmail) {
            debounceTimer = setTimeout(() => {
                checkUsernameAvailability(email?.trim());
            }, 1000);
        } else {
            setIsEmailAvailable(null);
            setShowErrorMessage(false);
        }

        return () => clearTimeout(debounceTimer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, touched]);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setEmail(value);
        setTouched(true);
        setIsEmailAvailable(null);
        setIsEmailExist && setIsEmailExist(null);
        setShowErrorMessage(false);
        setShowSuccessMessage(false);
        const isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/gm.test(value);
        e.target.setCustomValidity(isValid ? "" : "Please enter a valid email");
    };

    const checkUsernameAvailability = (email: string) => {
        getEmail(email)
            .then((response: boolean) => {
                setIsEmailAvailable(!response);
                setIsEmailExist && setIsEmailExist(response);
                setShowErrorMessage(response);
                if (!response) {
                    setShowSuccessMessage(true);
                    setTimeout(() => {
                        setShowSuccessMessage(false);
                    }, 3000);
                }
                if (inputRef.current) {
                    inputRef.current.setCustomValidity(response ? "Email is already exist" : "");
                }
            })
            .catch((error: Error) => {
                console.error('Error checking email availability:', error);
                setIsEmailAvailable(null);
                setShowErrorMessage(false);
                setShowSuccessMessage(false);
            });
    };

    return (
        <div className={className.parentDiv}>
            <label className={className.label}>Email<span className="text-red-500">*</span></label>
            <input
                ref={inputRef}
                name="email"
                type="email"
                required
                className={className.input}
                placeholder="Email"
                value={email}
                onChange={handleEmailChange}
                autoComplete="new-email"
            />
            {showSuccessMessage && isEmailAvailable === true && (
                <div className="flex items-center mt-2 text-xs text-green-500">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        className="mr-1"
                        fill="currentColor"
                    >
                        <circle cx="12" cy="12" r="10" opacity="0.15" />
                        <path d="M10.0 15.0L6.8 11.8L8.2 10.4L10.0 12.2L15.8 6.4L17.2 7.8L10.0 15.0Z" />
                    </svg>
                    <span>Email is available</span>
                </div>
            )}
            {showErrorMessage && isEmailAvailable === false && (
                <div className="flex items-center mt-2 text-xs text-red-500">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        className="mr-1"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
                    </svg>
                    <span>Email is already exist</span>
                </div>

            )}
        </div>
    );
};

export default EmailValidatedToCheckExistComponent;